import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authUtils } from "@/utils/auth";

/** Events that count as user activity */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
  "click",
];

/** Minimum gap between activity-driven writes to localStorage (ms) */
const ACTIVITY_THROTTLE_MS = 1000;

/** How often the expiry timestamp is checked (ms) */
const CHECK_INTERVAL_MS = 1000;

/**
 * Enforces the idle session timeout.
 *
 * - Any user activity resets the shared `idle_expiry` timestamp in
 *   localStorage (now + IDLE_TIMEOUT_MS).
 * - Every second the expiry is checked; when it passes, the auth token is
 *   removed and the user is redirected to /login.
 * - The expiry timestamp lives in localStorage, so all tabs share the same
 *   deadline — activity in one tab resets the timer for every tab, and a
 *   `storage` event listener catches a token removed by any other tab.
 */
const useIdleTimeout = (): void => {
  const navigate = useNavigate();
  const lastActivityWrite = useRef(0);

  useEffect(() => {
    const expireSession = () => {
      authUtils.removeToken();
      authUtils.clearIdleExpiry();
      navigate("/login", { replace: true });
    };

    // If there is no expiry yet (e.g. logged in before this feature), start one
    if (authUtils.getIdleExpiry() === null) {
      authUtils.refreshIdleExpiry();
    }

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityWrite.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityWrite.current = now;
      if (authUtils.isAuthenticated()) {
        authUtils.refreshIdleExpiry();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      // Another tab logged out or the session expired elsewhere
      if (event.key === "auth_token" && event.newValue === null) {
        authUtils.clearIdleExpiry();
        navigate("/login", { replace: true });
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );
    window.addEventListener("storage", handleStorage);

    const intervalId = window.setInterval(() => {
      if (!authUtils.isAuthenticated() || authUtils.isIdleExpired()) {
        expireSession();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(intervalId);
    };
  }, [navigate]);
};

export default useIdleTimeout;
